/**
 * Track 4.18 — Tenant switcher in the top nav.
 *
 * Shows the user's currently-scoped CDM tenant (org name + kind badge)
 * with a dropdown to switch to any other org they have access to via
 * cdm_role_grants. The chosen org_id is written to localStorage under
 * `cdm-org-id`; the api.ts request interceptor adds the matching
 * `X-CDM-Org-Id` header to every subsequent backend call. The auth
 * layer's _resolve_org_id honours that header (with allowed-orgs
 * validation), so all CDM page reads scope to the chosen tenant.
 */
import { useEffect, useRef, useState } from 'react'
import { Building2, ChevronDown, Globe, Check } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../../lib/api'

interface OrgRow {
  id: string
  name: string
  kind: string
  jurisdiction: string | null
  regulatory_zone: string | null
  data_residency: string | null
  isolation_mode: string | null
}

interface MeOrgsResponse {
  count: number
  orgs: OrgRow[]
  default_org_id: string | null
  global_view: boolean
  admin: boolean
}

const KIND_TONE: Record<string, string> = {
  group:          'bg-blue/10 text-blue-light border-blue/30',
  entity:         'bg-emerald/10 text-emerald border-emerald/30',
  business_unit:  'bg-cyan/10 text-cyan border-cyan/30',
  ring_fenced:    'bg-rose/10 text-rose-light border-rose/30',
}

function KindBadge({ kind }: { kind: string }) {
  return (
    <span className={clsx(
      'badge text-[8px] uppercase tracking-wider border px-1 py-0.5',
      KIND_TONE[kind] || 'bg-surface-raised text-text-muted border-border',
    )}>{kind.replace('_', ' ')}</span>
  )
}

export default function TenantSwitcher() {
  const [resp, setResp] = useState<MeOrgsResponse | null>(null)
  const [open, setOpen] = useState(false)
  const [chosenId, setChosenId] = useState<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem('cdm-org-id') : null,
  )
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    api.get<MeOrgsResponse>('/api/cdm/me/orgs')
      .then(({ data }) => setResp(data))
      .catch(() => setResp(null))
  }, [])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  if (!resp || resp.count === 0) {
    // No grants → no switcher. The default group fallback in
    // _resolve_org_id keeps single-tenant deployments working.
    return null
  }

  const currentId = chosenId || resp.default_org_id || (resp.orgs[0]?.id ?? null)
  const current = resp.orgs.find(o => o.id === currentId) || resp.orgs[0]

  // Don't render the switcher chrome at all when the user only has one
  // accessible tenant — there's nothing to switch to.
  const onlyOne = resp.orgs.length === 1
  if (!current) return null

  const choose = (orgId: string | null) => {
    if (orgId) {
      try { localStorage.setItem('cdm-org-id', orgId) } catch {}
      setChosenId(orgId)
    } else {
      try { localStorage.removeItem('cdm-org-id') } catch {}
      setChosenId(null)
    }
    setOpen(false)
    // Refresh the page so every CDM-scoped query re-runs against the
    // newly-chosen tenant. Pages otherwise cache the first response.
    if (typeof window !== 'undefined') window.location.reload()
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !onlyOne && setOpen(o => !o)}
        disabled={onlyOne}
        className={clsx(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-surface-raised text-[10px] transition-colors',
          !onlyOne && 'hover:bg-surface-hover cursor-pointer',
          onlyOne && 'opacity-80 cursor-default',
        )}
        title={onlyOne ? 'Single-tenant scope' : 'Switch CDM tenant'}>
        <Building2 size={11} className="text-blue-light" />
        <span className="font-medium text-text-primary max-w-[160px] truncate">{current.name}</span>
        <KindBadge kind={current.kind} />
        {!onlyOne && <ChevronDown size={10} className="text-text-muted" />}
      </button>

      {open && !onlyOne && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-surface border border-border rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <Globe size={11} className="text-text-muted" />
            <span className="text-[10px] uppercase tracking-wider text-text-muted">
              {resp.global_view ? 'Global view · select tenant' : 'Your tenants'}
            </span>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {resp.orgs.map(o => {
              const active = o.id === current.id
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => choose(o.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2 border-b border-border/50 last:border-0 hover:bg-surface-hover transition-colors',
                    active && 'bg-blue/5',
                  )}>
                  <div className="flex items-center gap-2">
                    <Check size={11} className={active ? 'text-blue-light' : 'opacity-0'} />
                    <span className="flex-1 min-w-0 text-[11px] font-medium text-text-primary truncate">{o.name}</span>
                    <KindBadge kind={o.kind} />
                  </div>
                  <div className="ml-5 flex items-center gap-2 text-[9px] text-text-muted mt-0.5">
                    {o.jurisdiction && <span className="font-mono">{o.jurisdiction}</span>}
                    {o.regulatory_zone && <span>{o.regulatory_zone}</span>}
                    {o.data_residency && <span>{o.data_residency}</span>}
                    {o.isolation_mode && o.isolation_mode !== 'shared' && (
                      <span className="text-rose-light">{o.isolation_mode}</span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
          {resp.global_view && resp.default_org_id && current.id !== resp.default_org_id && (
            <button
              type="button"
              onClick={() => choose(null)}
              className="w-full text-left px-3 py-2 border-t border-border bg-surface-raised hover:bg-surface-hover text-[10px] text-text-muted">
              Reset to default group
            </button>
          )}
        </div>
      )}
    </div>
  )
}
